import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography
} from '@material-ui/core'
import {
  getValueFromLocalStorage,
  isEmpty,
  setValueInLocalStorage
} from '@openhouse-technologies/utils'
import PropTypes from 'prop-types'
import React, { useState, useEffect } from 'react'
import { useCart } from 'react-use-cart'

import { handleBookEnrolment } from 'api'
import ModalForm from 'app/components/ModalForm'
import { navigationPaths } from 'app/config'
import { alertDialog } from 'app/helpers'
import {
  BOOKING_PAYMENT_TYPES,
  DEFAULT_EMANDATE_METHOD
} from 'app/pages/Class/Inventory/constants'
import MasterForm from 'app/pages/User/Parent/MasterForm'

import BookWithCoins from './components/BookWithCoins'
import BookWithEmandate from './components/BookWithEmandate'
import BookWithRupees from './components/BookWithRupees'
import ProgramRupees from './components/ProgramRupees'
import SetupEmandateAndBook from './components/SetupEmandateAndBook'
import * as Styles from './styles'

const CheckoutModal = ({
  isModalOpen,
  onModalClose,
  checkoutType,
  paymentOptions,
  coinSummary,
  excelPaymentSummary,
  upcomingExcelPaymentSummary = [],
  kitItems = []
}) => {
  const activeOption = paymentOptions.find(
    (option) => option.payment_type === checkoutType
  )
  const { cartTotal, emptyCart, items: cartItems } = useCart()

  // Charmi/user feedback (2026-05-27) — replace the post-submit Swal2
  // dialog with an in-component MUI Dialog so the buttons / typography
  // match the rest of Alfred (CANCEL/SUBMIT in modify-admission, etc.).
  const [paymentLinkDialog, setPaymentLinkDialog] = useState(null) // { paymentLink, ordersUrl, whatsappSent }
  // { chargedNow, paymentStatus, deadline, ordersUrl } — result of an autopay booking.
  const [emandateDialog, setEmandateDialog] = useState(null)

  const selectedParentDetails = getValueFromLocalStorage('CART_PARENT')
  const parent = selectedParentDetails?.uuid
  const coinBalance = coinSummary?.remaining_coins
  const [formData, setFormData] = useState({
    user: selectedParentDetails,
    uuid: parent,
    cart_type: 'booking',
    payment_summary: activeOption?.summary,
    coin_summary: {},
    reason: '',
    coins: 0,
    use_referral_code: false,
    is_policy_acknowledged_alfred: false,
    reason_for_using_alfred: '',
    show_reason_field: true
  })

  const showReasonField = !cartItems?.some(
    (item) =>
      item?.batchDetails?.course?.subjects?.[0]?.progress_enabled === false
  )

  useEffect(() => {
    setFormData({
      user: selectedParentDetails,
      uuid: selectedParentDetails?.uuid,
      cart_type: 'topup_booking',
      payment_summary: activeOption?.summary,
      coin_summary: coinSummary,
      reason: '',
      coins: 0,
      use_referral_code: false,
      is_policy_acknowledged_alfred: false,
      show_reason_field: showReasonField
    })
  }, [parent, activeOption, coinSummary, showReasonField])

  const onModalCloseLocal = () => {
    setReasonError('')
    setReasonForUsingAlfred('')
    onModalClose()
  }

  const summary = activeOption?.summary ?? {}
  const summary_with_coins = activeOption?.summary_with_coins ?? {}

  const [useCoins, setUseCoins] = useState(false)
  const [reasonForUsingAlfred, setReasonForUsingAlfred] = useState('')
  const [reasonError, setReasonError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  // Razorpay needs the rail before the link exists; UPI covers most parents.
  const [emandateMethod, setEmandateMethod] = useState(DEFAULT_EMANDATE_METHOD)

  const setReasonForUsingAlfredHandler = (val) => {
    setReasonForUsingAlfred(val)
    if (!isEmpty(val)) {
      setReasonError('')
    }
  }

  const validateReason = () => {
    if (showReasonField) {
      if (isEmpty(reasonForUsingAlfred)) {
        setReasonError('This field is required')
        return false
      }
    }
    return true
  }

  useEffect(() => {
    if (isModalOpen) {
      setUseCoins(false)
    }
  }, [isModalOpen])

  const setUseCoinsHandler = (val) => setUseCoins(val)

  const bookWithCoinsExists = paymentOptions?.some(
    (opt) => opt.payment_type === BOOKING_PAYMENT_TYPES.BOOK_WITH_COINS
  )

  const disableUseCoins = bookWithCoinsExists || coinBalance === 0

  const activeSummary = useCoins ? summary_with_coins : summary

  const renderContent = () => {
    switch (checkoutType) {
      case BOOKING_PAYMENT_TYPES.BOOK_WITH_COINS:
        return (
          <BookWithCoins
            summary={summary}
            reasonForUsingAlfred={reasonForUsingAlfred}
            setReasonForUsingAlfredHandler={setReasonForUsingAlfredHandler}
            reasonError={reasonError}
            showReasonField={showReasonField}
            excelPaymentSummary={excelPaymentSummary}
          />
        )
      case BOOKING_PAYMENT_TYPES.BOOK_WITH_RUPEES:
        return (
          <BookWithRupees
            activeSummary={activeSummary}
            useCoins={useCoins}
            disableUseCoins={disableUseCoins}
            setUseCoinsHandler={setUseCoinsHandler}
            reasonForUsingAlfred={reasonForUsingAlfred}
            setReasonForUsingAlfredHandler={setReasonForUsingAlfredHandler}
            reasonError={reasonError}
            showReasonField={showReasonField}
            excelPaymentSummary={excelPaymentSummary}
          />
        )
      case BOOKING_PAYMENT_TYPES.BOOK_WITH_EMANDATE:
        return (
          <BookWithEmandate
            activeOption={activeOption}
            excelPaymentSummary={excelPaymentSummary}
            upcomingExcelPaymentSummary={upcomingExcelPaymentSummary}
            reasonForUsingAlfred={reasonForUsingAlfred}
            setReasonForUsingAlfredHandler={setReasonForUsingAlfredHandler}
            reasonError={reasonError}
            showReasonField={showReasonField}
          />
        )

      case BOOKING_PAYMENT_TYPES.SETUP_EMANDATE_AND_PAY:
        return (
          <SetupEmandateAndBook
            activeOption={activeOption}
            excelPaymentSummary={excelPaymentSummary}
            upcomingExcelPaymentSummary={upcomingExcelPaymentSummary}
            emandateMethod={emandateMethod}
            setEmandateMethodHandler={setEmandateMethod}
            reasonForUsingAlfred={reasonForUsingAlfred}
            setReasonForUsingAlfredHandler={setReasonForUsingAlfredHandler}
            reasonError={reasonError}
            showReasonField={showReasonField}
          />
        )

      case BOOKING_PAYMENT_TYPES.RECHARGE_AND_PAY:
        return

      case BOOKING_PAYMENT_TYPES.PROGRAM_RUPEES:
        return <ProgramRupees summary={activeSummary} />

      default:
        return <p>Invalid checkout type</p>
    }
  }

  const handleBookWithCoins = async () => {
    if (!validateReason()) {
      return
    }

    try {
      setSubmitting(true)
      const result = await handleBookEnrolment({
        summary: activeSummary,
        parent,
        cartItems: [...cartItems, ...kitItems],
        cartTotal,
        payment_type: checkoutType,
        useCoins,
        coinBalance,
        reason_for_using_alfred: reasonForUsingAlfred
      })
      if (result?.success) {
        emptyCart()
        setValueInLocalStorage('CART_PARENT', {})
        window.open(`${window.location.origin}${navigationPaths.orders}`)
      }
    } catch (error) {
      alertDialog({ text: error?.message, title: '', icon: 'error' })
    } finally {
      setSubmitting(false)
    }
    onModalCloseLocal()
  }

  // Autopay booking: the debit starts now but usually settles later (Razorpay documents
  // 24-36h for UPI), so the dialog reports what the money is doing rather than claiming a
  // booking. A plain "booked!" would be a lie until the capture lands.
  const handleBookWithEmandate = async () => {
    if (!validateReason()) {
      return
    }

    try {
      setSubmitting(true)
      const result = await handleBookEnrolment({
        summary,
        parent,
        cartItems: [...cartItems, ...kitItems],
        cartTotal,
        payment_type: checkoutType,
        reason_for_using_alfred: reasonForUsingAlfred
      })
      if (result?.success) {
        emptyCart()
        setValueInLocalStorage('CART_PARENT', {})
        const mandateCharge = result?.data?.mandate_charge || {}
        setEmandateDialog({
          chargedNow: Boolean(mandateCharge.charged_now),
          paymentStatus: mandateCharge.payment_status,
          deadline: mandateCharge.deadline,
          ordersUrl: `${window.location.origin}${navigationPaths.orders}`
        })
      }
    } catch (error) {
      alertDialog({ text: error?.message, title: '', icon: 'error' })
    } finally {
      setSubmitting(false)
    }
    onModalCloseLocal()
  }

  // Mandate setup + first payment. The registration link is the deliverable: it is
  // WhatsApped to the parent by the backend and shown here so the centre can send it
  // themselves too. It also lands on invoice.payment_link, so Orders keeps a copy.
  const handleSetupEmandateAndBook = async () => {
    if (!validateReason()) {
      return
    }

    try {
      setSubmitting(true)
      const result = await handleBookEnrolment({
        summary,
        parent,
        cartItems: [...cartItems, ...kitItems],
        cartTotal,
        payment_type: checkoutType,
        mandateMaxAmount: activeOption?.max_amount,
        emandateMethod,
        reason_for_using_alfred: reasonForUsingAlfred
      })
      if (result?.success) {
        emptyCart()
        setValueInLocalStorage('CART_PARENT', {})
        const data = result?.data || {}
        setPaymentLinkDialog({
          paymentLink: data.short_url || data.payment_link,
          whatsappSent: Boolean(data?.delivery?.whatsapp_sent),
          ordersUrl: `${window.location.origin}${navigationPaths.orders}`
        })
      }
    } catch (error) {
      alertDialog({ text: error?.message, title: '', icon: 'error' })
    } finally {
      setSubmitting(false)
    }
    onModalCloseLocal()
  }

  const handleBookProgram = async () => {
    setSubmitting(true)
    try {
      const result = await handleBookEnrolment({
        cartItems,
        payment_type: checkoutType,
        summary: activeSummary,
        parent
      })
      if (result?.success) {
        emptyCart()
        setValueInLocalStorage('CART_PARENT', {})
        // Charmi #5 (2026-05-26) — popup-blocker was eating the prior
        // window.open(/orders) after the awaited submit. Surface the
        // Razorpay link in a dialog with explicit "open orders" + "copy
        // link" actions instead. User feedback (2026-05-27) — the Swal2
        // version of this dialog rendered tiny ungainly buttons that
        // didn't match Alfred's MUI styling and clipped the URL with
        // word-break. Re-implement as an in-component MUI Dialog (see
        // paymentLinkDialog state + render at the end of this component)
        // so it matches the modify-admission modal's CANCEL/SUBMIT
        // pattern and the URL renders cleanly.
        const invoice = result?.data || {}
        const paymentLink = invoice.payment_link
        const ordersUrl = `${window.location.origin}${navigationPaths.orders}`
        setPaymentLinkDialog({ paymentLink, ordersUrl })
      }
    } catch (error) {
      alertDialog({ text: error?.message, title: '', icon: 'error' })
    } finally {
      setSubmitting(false)
    }
    onModalCloseLocal()
  }

  const handleRechargeAndPay = async (newData, action) => {
    try {
      const result = await handleBookEnrolment({
        ...newData,
        cartItems: [...cartItems, ...kitItems],
        payment_type: checkoutType,
        summary: activeSummary
      })
      if (result?.success) {
        emptyCart()
        setValueInLocalStorage('CART_PARENT', {})
        window.open(`${window.location.origin}${navigationPaths.orders}`)
      }
    } catch (error) {
      alertDialog({ text: error?.message, title: '', icon: 'error' })
    }
    onModalCloseLocal()
  }

  const renderActions = () => (
    <Styles.ActionsContainer>
      {checkoutType === BOOKING_PAYMENT_TYPES.BOOK_WITH_COINS && (
        <Styles.ActionButton
          disabled={submitting}
          onClick={handleBookWithCoins}
        >
          confirm booking
        </Styles.ActionButton>
      )}
      {checkoutType === BOOKING_PAYMENT_TYPES.BOOK_WITH_RUPEES && (
        <Styles.ActionButton
          disabled={submitting}
          onClick={handleBookWithCoins}
        >
          generate payment link
        </Styles.ActionButton>
      )}
      {checkoutType === BOOKING_PAYMENT_TYPES.BOOK_WITH_EMANDATE && (
        <Styles.ActionButton
          disabled={submitting}
          onClick={handleBookWithEmandate}
        >
          start auto-debit & book
        </Styles.ActionButton>
      )}
      {checkoutType === BOOKING_PAYMENT_TYPES.SETUP_EMANDATE_AND_PAY && (
        <Styles.ActionButton
          disabled={submitting}
          onClick={handleSetupEmandateAndBook}
        >
          send e-mandate setup link
        </Styles.ActionButton>
      )}
      {checkoutType === BOOKING_PAYMENT_TYPES.PROGRAM_RUPEES && (
        <Styles.ActionButton disabled={submitting} onClick={handleBookProgram}>
          generate payment link
        </Styles.ActionButton>
      )}
      {checkoutType === BOOKING_PAYMENT_TYPES.RECHARGE_AND_PAY && null}
    </Styles.ActionsContainer>
  )

  const renderEmandateDialog = () => {
    if (!emandateDialog) return null
    const { chargedNow, paymentStatus, deadline, ordersUrl } = emandateDialog
    return (
      <Dialog
        open
        onClose={() => setEmandateDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {chargedNow ? 'Booked' : 'Auto-debit started'}
        </DialogTitle>
        <DialogContent>
          {chargedNow ? (
            <Typography variant="body1">
              The debit went through and the class is booked.
            </Typography>
          ) : (
            <Typography variant="body1">
              The auto-debit has been submitted to the parent&apos;s bank
              {paymentStatus ? ` (status: ${paymentStatus})` : ''}. UPI auto-pay
              usually settles in <strong>24&ndash;36 hours</strong>; the class
              is booked automatically the moment the money is collected. Until
              then the order stays pending in Orders.
              {deadline
                ? ` If nothing settles by ${new Date(deadline).toLocaleString(
                    'en-IN'
                  )}, the booking is cancelled and the centre is notified.`
                : ''}
            </Typography>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px 20px' }}>
          <Button
            onClick={() => {
              setEmandateDialog(null)
              window.open(ordersUrl)
            }}
            color="primary"
            variant="contained"
            autoFocus
          >
            open orders
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  const renderPaymentLinkDialog = () => {
    if (!paymentLinkDialog) return null
    const { paymentLink, ordersUrl, whatsappSent } = paymentLinkDialog
    return (
      <Dialog
        open
        onClose={() => setPaymentLinkDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Payment link generated</DialogTitle>
        <DialogContent>
          {whatsappSent ? (
            <Typography variant="body2" style={{ marginBottom: 8 }}>
              Sent to the parent on WhatsApp. Share it here as well if needed.
            </Typography>
          ) : null}
          {paymentLink ? (
            <Typography
              variant="body2"
              component="div"
              style={{
                wordBreak: 'break-all',
                fontFamily: 'monospace',
                background: '#f6f6f6',
                padding: '12px 14px',
                borderRadius: 4,
                marginTop: 4
              }}
            >
              {paymentLink}
            </Typography>
          ) : (
            <Typography variant="body1">
              Order created. Open Orders to view the Razorpay link.
            </Typography>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px 20px' }}>
          {paymentLink && (
            <Button
              onClick={() => {
                if (navigator.clipboard) {
                  navigator.clipboard.writeText(paymentLink)
                }
              }}
              color="default"
            >
              copy link
            </Button>
          )}
          <Button
            onClick={() => {
              setPaymentLinkDialog(null)
              window.open(ordersUrl)
            }}
            color="primary"
            variant="contained"
            autoFocus
          >
            open orders
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <>
      {checkoutType === BOOKING_PAYMENT_TYPES.RECHARGE_AND_PAY ? (
        <MasterForm
          isModalOpen={isModalOpen}
          onSaveClick={handleRechargeAndPay}
          closeModal={onModalCloseLocal}
          action={'topup'}
          cartType={'booking'}
          coinSummary={coinSummary}
          excelPaymentSummary={excelPaymentSummary}
          data={formData}
          buttonLabel="generate payment link"
          showReasonField={showReasonField}
        />
      ) : (
        <ModalForm
          isModalOpen={isModalOpen}
          enableSave={false}
          showCloseIcon
          onModalClose={onModalCloseLocal}
          modalTitle="checkout"
          containerStyles={{ padding: 12 }}
        >
          <Styles.Container>
            {renderContent()}
            {renderActions()}
          </Styles.Container>
        </ModalForm>
      )}
      {renderPaymentLinkDialog()}
      {renderEmandateDialog()}
    </>
  )
}
export default CheckoutModal
